import dynamic from 'next/dynamic';

import { env } from '@natu/env';
import { Typography } from '@natu/ui/Typography';

import { StoryblokRichtext, type StoryblokRichTextData } from '../../../../../richtext';
import { isDraftMode } from '../../../../../storyblok-preview';
import {
  getLinkPropsFromStoryblok,
  sbEditable,
  type SBProps,
  type StoryblokLink,
} from '../../../../../utils';

const CompetitorReportRequestFormContent = dynamic(() =>
  import('./CompetitorReportRequestFormContent').then(
    module => module.CompetitorReportRequestFormContent,
  ),
);

interface SBCompetitorReportRequestFormProps {
  event_form_name?: string;
  event_internal_campaign_name?: string;
  formTitle?: string;
  id?: string;
  introCopy?: StoryblokRichTextData;
  processingConsent?: StoryblokRichTextData;
  sendEvent?: boolean;
  submitLabel?: string;
  successPageLink?: StoryblokLink;
}

const hasRichTextContent = (value?: StoryblokRichTextData): boolean => {
  if (!value || !Array.isArray(value.content)) return false;

  const hasContent = (content: unknown): boolean => {
    if (!content || typeof content !== 'object') return false;

    const node = content as { content?: unknown; text?: unknown };

    if (typeof node.text === 'string' && node.text.trim().length > 0) return true;
    if (!Array.isArray(node.content)) return false;

    return node.content.some(hasContent);
  };

  return value.content.some(hasContent);
};

const resolveSuccessDestination = (successPageLink?: StoryblokLink): string | undefined => {
  const cachedUrl = successPageLink?.cached_url?.trim();

  if (!successPageLink || successPageLink.linktype !== 'story' || !cachedUrl) return undefined;

  const { href } = getLinkPropsFromStoryblok({ ...successPageLink, cached_url: cachedUrl });

  try {
    const applicationOrigin = new URL(env.NEXT_PUBLIC_APP_URL).origin;
    const destination = new URL(href, applicationOrigin);

    if (destination.origin !== applicationOrigin) return undefined;

    return `${destination.pathname}${destination.search}${destination.hash}`;
  } catch {
    return undefined;
  }
};

export const SBCompetitorReportRequestForm = ({
  blok,
}: SBProps<SBCompetitorReportRequestFormProps>) => {
  const {
    event_form_name,
    event_internal_campaign_name,
    formTitle,
    id,
    introCopy,
    processingConsent,
    sendEvent,
    submitLabel,
    successPageLink,
  } = blok;
  const hasProcessingConsent = hasRichTextContent(processingConsent);
  const configuredSubmitLabel =
    typeof submitLabel === 'string' && submitLabel.trim() ? submitLabel : undefined;
  const formId = typeof id === 'string' && id.trim() ? id.trim() : undefined;
  const analyticsEnabled = sendEvent !== false;
  const eventFormName =
    typeof event_form_name === 'string' && event_form_name.trim()
      ? event_form_name.trim()
      : undefined;
  const eventInternalCampaignName =
    typeof event_internal_campaign_name === 'string' && event_internal_campaign_name.trim()
      ? event_internal_campaign_name.trim()
      : undefined;
  const successDestination = resolveSuccessDestination(successPageLink);

  if (
    !hasProcessingConsent ||
    !configuredSubmitLabel ||
    (analyticsEnabled && (!eventFormName || !eventInternalCampaignName))
  ) {
    const missingFields = [
      !hasProcessingConsent && 'processingConsent',
      !configuredSubmitLabel && 'submitLabel',
      analyticsEnabled && !eventFormName && 'event_form_name',
      analyticsEnabled && !eventInternalCampaignName && 'event_internal_campaign_name',
    ].filter((field): field is string => Boolean(field));
    const showDiagnostic = Boolean(blok._editable) || isDraftMode(false);

    return (
      <div
        className="rounded-2xl bg-white px-6 py-8 shadow-2xl xl:px-10 xl:py-14"
        {...sbEditable(blok)}
      >
        <p role="alert">
          {showDiagnostic
            ? `Competitor Report Request form is missing required Storyblok content: ${missingFields.join(', ')}.`
            : 'This form is temporarily unavailable. Please try again later.'}
        </p>
      </div>
    );
  }

  return (
    <div id={formId} className="scroll-mt-24" {...sbEditable(blok)}>
      <CompetitorReportRequestFormContent
        event_form_name={eventFormName}
        event_internal_campaign_name={eventInternalCampaignName}
        formTitle={
          formTitle ? (
            <Typography
              className="mb-6 font-semibold"
              component="h2"
              variant={['display-sm-28', 'display-sm-28', 'display-lg-46']}
            >
              {formTitle}
            </Typography>
          ) : undefined
        }
        introCopy={
          introCopy ? (
            <StoryblokRichtext className="mb-8 text-gray-700" data={introCopy} />
          ) : undefined
        }
        processingConsent={<StoryblokRichtext className="inline" data={processingConsent} />}
        reCaptchaKey={env.RECAPTCHA_SITE_KEY}
        sendEvent={analyticsEnabled}
        submitLabel={configuredSubmitLabel}
        successDestination={successDestination}
      />
    </div>
  );
};
